import { useEffect, useState } from "react";
import {
  PayPalScriptProvider,
  PayPalCardFieldsProvider,
  PayPalNumberField,
  PayPalExpiryField,
  PayPalCVVField,
  PayPalNameField,
  usePayPalCardFields,
} from "@paypal/react-paypal-js";
import api from "../lib/api";
import { toast } from "sonner";
import { CreditCard, ShieldCheck, Loader2 } from "lucide-react";

/* ─────────────────────────────────────────────────────────────────
 * In-app credit-card checkout — PayPal Advanced Card Fields (ACDC).
 *
 * Mounted only when the current order is in an ACDC-eligible country
 * (Phase 1: Israel). If the /card-order/create endpoint returns
 * HTTP 409 code=acdc_not_available, the parent falls back to the
 * standard PayPal redirect checkout.
 *
 * Flow:
 *   1. Fetch a short-lived client_token + client_id via
 *      POST /api/payments/paypal/client-token.
 *   2. Bootstrap the PayPal JS SDK with data-client-token.
 *   3. Render <CardFieldsProvider> containing the card fields. The
 *      user types their card details directly inside Drivons — the
 *      values are tokenised in the browser and never touch our server.
 *   4. On Pay tap:
 *        POST /api/payments/paypal/card-order/create → paypal_order_id
 *        cardFields.submit()                          → PayPal charges
 *        POST /api/payments/paypal/capture/{id}      → order → Paid
 *   5. onSuccess() is invoked; the parent navigates to /order/{id}.
 *
 * Props:
 *   orderId          — internal Drivons order id
 *   amount, currency — for the Pay button label
 *   onSuccess(data)  — called after capture succeeds
 *   onFallback(err)  — called when ACDC is not available or user chose
 *                       to switch back to redirect flow
 *   onCancel()       — called when user closes without paying
 * ────────────────────────────────────────────────────────────── */

// Inner component — must live inside <PayPalCardFieldsProvider> to access the hook.
function PayNowButton({ amount, currency, submitting, onPay }) {
  const { cardFieldsForm } = usePayPalCardFields();
  const disabled = submitting || !cardFieldsForm;
  return (
    <button
      type="button"
      onClick={() => onPay(cardFieldsForm)}
      disabled={disabled}
      data-testid="paypal-card-pay-btn"
      className={[
        "w-full mt-4 rounded-2xl px-4 py-3 font-black text-black transition",
        disabled
          ? "bg-brand-accent/60 cursor-not-allowed"
          : "bg-brand-accent hover:brightness-110 active:scale-[.99]",
      ].join(" ")}
    >
      {submitting ? (
        <span className="inline-flex items-center gap-2">
          <Loader2 size={16} className="animate-spin" />
          Processing…
        </span>
      ) : (
        <span className="inline-flex items-center gap-2">
          <CreditCard size={16} />
          Pay {Number(amount).toFixed(2)} {currency}
        </span>
      )}
    </button>
  );
}

export default function PayPalCardCheckout({ orderId, amount, currency, onSuccess, onFallback, onCancel }) {
  const [bootstrap, setBootstrap] = useState(null); // {client_token, client_id, mode}
  const [bootErr, setBootErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [threeDsWait, setThreeDsWait] = useState(false);

  // Fetch a fresh client token every mount (single-session token — must
  // not be cached across renders/users).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/payments/paypal/client-token", {});
        if (!cancelled) setBootstrap(data);
      } catch (e) {
        if (cancelled) return;
        const detail = e?.response?.data?.detail;
        setBootErr(typeof detail === "string" ? detail : "Could not initialise card checkout");
        if (onFallback) onFallback(e);
      }
    })();
    return () => { cancelled = true; };
  }, [onFallback]);

  const handlePay = async (cardFieldsForm) => {
    if (!cardFieldsForm) return;
    setSubmitting(true);
    try {
      // 1. Ask backend to create a PayPal order for this Drivons order.
      //    Country ineligibility surfaces as HTTP 409 acdc_not_available.
      //    For TR / AE the fare is in TRY / AED — PayPal only settles a
      //    fixed list of currencies, so the backend requires explicit FX
      //    consent (409 conversion_consent_required) before charging in
      //    USD. We fetch the quote, show a native confirm() modal with
      //    the exact rate, and only proceed after the customer accepts.
      let paypalOrderId;
      let confirmed = false;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const { data: created } = await api.post("/payments/paypal/card-order/create", {
            order_id: orderId,
            origin_url: window.location.origin,
            confirmed_conversion: confirmed,
          });
          paypalOrderId = created.paypal_order_id;
          break;
        } catch (e) {
          const detail = e?.response?.data?.detail;
          if (detail && typeof detail === "object" && detail.code === "acdc_not_available") {
            setSubmitting(false);
            if (onFallback) onFallback(e);
            return;
          }
          if (detail && typeof detail === "object" && detail.code === "conversion_consent_required" && attempt === 0) {
            // Fetch quote and ask the buyer to accept USD conversion.
            try {
              const { data: q } = await api.get(`/payments/paypal/quote/${orderId}`);
              if (q.will_convert) {
                const orig = q.original; const pp = q.paypal;
                const fx = Number(q.fx_rate);
                const rateLabel = fx < 1
                  ? `1 ${orig.currency} ≈ ${fx.toFixed(4)} ${pp.currency}`
                  : `1 ${pp.currency} ≈ ${(1/fx).toFixed(4)} ${orig.currency}`;
                const msg =
                  `PayPal will charge in USD\n\n` +
                  `Your fare: ${orig.amount.toFixed(2)} ${orig.currency}\n` +
                  `PayPal charge: ${pp.amount.toFixed(2)} ${pp.currency}\n` +
                  `Rate: ${rateLabel}\n` +
                  `Source: ${q.fx_source}\n\n` +
                  `Continue with this conversion?`;
                if (!window.confirm(msg)) {
                  setSubmitting(false);
                  if (onCancel) onCancel();
                  return;
                }
                confirmed = true;
                continue;
              }
            } catch (qe) {
              setSubmitting(false);
              if (onFallback) onFallback(qe);
              return;
            }
          }
          throw e;
        }
      }
      if (!paypalOrderId) {
        setSubmitting(false);
        return;
      }

      // 2. Ask the SDK to submit the card details (may open a 3DS
      //    challenge modal, handled entirely by the SDK).
      setThreeDsWait(true);
      try {
        await cardFieldsForm.submit({ orderId: paypalOrderId });
      } finally {
        setThreeDsWait(false);
      }

      // 3. Capture the order server-side and mark the Drivons order paid.
      const { data: cap } = await api.post(`/payments/paypal/capture/${paypalOrderId}`, {});
      toast.success("Payment received");
      if (onSuccess) onSuccess({ ...cap, paypal_order_id: paypalOrderId });
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.details?.[0]?.description ||
        e?.message ||
        "Card payment failed. Please check your details and try again.";
      toast.error(typeof msg === "string" ? msg : "Card payment failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (bootErr) {
    // Parent will have received onFallback; we render nothing.
    return null;
  }
  if (!bootstrap) {
    return (
      <div className="rounded-2xl border border-ink-border bg-ink-surface2 p-4 text-center text-white/70">
        <Loader2 size={18} className="animate-spin inline-block mr-2" />
        Preparing secure card form…
      </div>
    );
  }

  return (
    <div data-testid="paypal-card-checkout" className="rounded-2xl border border-ink-border bg-ink-surface2 p-4">
      <PayPalScriptProvider
        options={{
          "client-id":     bootstrap.client_id,
          "data-client-token": bootstrap.client_token,
          components:      "card-fields",
          intent:          "capture",
          currency:        (currency || "USD").toUpperCase(),
          // Live vs sandbox is inferred by PayPal from the client id.
        }}
      >
        <PayPalCardFieldsProvider
          createOrder={async () => {
            // Not used — we drive order creation ourselves in handlePay
            // so we can gate on ACDC eligibility and record fx metadata.
            // Returning a rejected promise here would break the SDK's
            // internal validation; instead we return a benign string —
            // the actual order id is passed via cardFieldsForm.submit().
            return "will-be-provided-at-submit";
          }}
          onApprove={() => { /* server-side capture drives success */ }}
          onError={(err) => {
            toast.error("Card checkout error: " + (err?.message || "unknown"));
          }}
        >
          <div className="space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-white/60 font-black">Cardholder name</label>
              <div className="mt-1 rounded-xl border border-ink-border bg-black/50 px-3 py-2 min-h-[42px]">
                <PayPalNameField />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-white/60 font-black">Card number</label>
              <div className="mt-1 rounded-xl border border-ink-border bg-black/50 px-3 py-2 min-h-[42px]">
                <PayPalNumberField />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wide text-white/60 font-black">Expiry</label>
                <div className="mt-1 rounded-xl border border-ink-border bg-black/50 px-3 py-2 min-h-[42px]">
                  <PayPalExpiryField />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-white/60 font-black">CVV</label>
                <div className="mt-1 rounded-xl border border-ink-border bg-black/50 px-3 py-2 min-h-[42px]">
                  <PayPalCVVField />
                </div>
              </div>
            </div>
          </div>

          <PayNowButton
            amount={amount}
            currency={currency}
            submitting={submitting || threeDsWait}
            onPay={handlePay}
          />

          <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-white/50">
            <ShieldCheck size={12} />
            Secured by PayPal · 3-D Secure when required · PCI-DSS Level 1
          </div>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              data-testid="paypal-card-cancel-btn"
              className="mt-2 w-full text-center text-[12px] text-white/50 hover:text-white/80"
            >
              Cancel and choose another payment method
            </button>
          )}
        </PayPalCardFieldsProvider>
      </PayPalScriptProvider>
    </div>
  );
}
